// A CASCADING group picker for the hierarchical player-group tree (site →
// building → floor → room → zone). It replaces the old flat <select> that
// lost the hierarchy. The widget is a stack of DEPENDENT kit `Select`s — one
// per active level — that let the user drill down the tree.
//
// Design: the component is fully CONTROLLED and render-derived. There is NO
// internal level state and NO effect. Every render computes the ancestor PATH
// of `value` within `tree` (findPath) and the level Selects fall out of it.
// `onChange` fires ONLY from a real user interaction (Radix `onValueChange`) —
// never from render — so a transient empty tree (still loading) while `value`
// is a real id can never clobber the selection.

import { useTranslation } from 'react-i18next';
import type { PlayerGroupNode } from '@displaycms/contracts';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { pickName } from '@/lib/i18n-name';

// Radix Select forbids empty-string item values — use sentinels and map
// to/from the domain meaning (mirrors components/composer/transition-control).
const NONE = '__none__'; // level 0: "no group at all" → group_id = null
const KEEP = '__keep__'; // level N+1: "the level-N node IS the chosen group"

export interface GroupCascaderProps {
  tree: PlayerGroupNode[];
  value: string | null;
  onChange: (groupId: string | null) => void;
  /**
   * Label for the level-0 "no group" option. Defaults to the pairing/detail
   * wording (`— Aucun groupe —`, meaning "remove from group"). In a FILTER
   * context the caller passes an "all / no filter" label instead so the
   * sentinel reads correctly (e.g. `Tous les groupes`).
   */
  noneLabel?: string;
  /** Disable every level Select (e.g. while a form submit is in flight). */
  disabled?: boolean;
  /**
   * testid for the container; level Selects get `${testId}-level-${idx}`.
   * Distinct per call site so two instances on one page (e.g. the list filter
   * + the pair dialog) don't collide.
   */
  testId?: string;
}

/** Ancestor chain (root → … → target) for `targetId`, or null if absent. */
function findPath(
  nodes: ReadonlyArray<PlayerGroupNode>,
  targetId: string,
): PlayerGroupNode[] | null {
  for (const node of nodes) {
    if (node.id === targetId) return [node];
    const childPath = findPath(node.children, targetId);
    if (childPath) return [node, ...childPath];
  }
  return null;
}

export function GroupCascader({
  tree,
  value,
  onChange,
  noneLabel,
  disabled,
  testId = 'group-cascader',
}: GroupCascaderProps): JSX.Element {
  const { t, i18n } = useTranslation('players');
  const lang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';

  // The selected ancestor chain. Empty when value is null or stale (not in the
  // tree) — we then show only level 0 on NONE, without emitting anything.
  const path = (value && findPath(tree, value)) || [];

  function kindSuffix(node: PlayerGroupNode): string | null {
    if (!node.kind) return null;
    return t(`group_cascader.kind.${node.kind}`, { defaultValue: node.kind });
  }

  function nodeOption(node: PlayerGroupNode): JSX.Element {
    const suffix = kindSuffix(node);
    return (
      <SelectItem key={node.id} value={node.id}>
        <span>{pickName(node.name_i18n, lang)}</span>
        {suffix ? (
          <span className="ml-2 text-xs text-muted-foreground">{suffix}</span>
        ) : null}
      </SelectItem>
    );
  }

  // Build the descriptor for every active level from the derived path. Level 0
  // always renders (roots + NONE). A level N+1 renders only when the node
  // chosen at level N has children (drill-down available).
  type Level = {
    options: ReadonlyArray<PlayerGroupNode>;
    selected: string;
    parent: PlayerGroupNode | null; // null at level 0
  };
  const levels: Level[] = [{ options: tree, selected: path[0]?.id ?? NONE, parent: null }];
  for (let i = 0; path[i] && path[i]!.children.length > 0; i += 1) {
    levels.push({
      options: path[i]!.children,
      selected: path[i + 1]?.id ?? KEEP,
      parent: path[i]!,
    });
  }

  function handleLevelChange(level: Level, levelIndex: number, next: string): void {
    if (levelIndex === 0) {
      onChange(next === NONE ? null : next);
      return;
    }
    // Deeper levels: KEEP means "stop here — the parent IS the chosen group".
    onChange(next === KEEP ? level.parent!.id : next);
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {levels.map((level, idx) => (
        <Select
          key={idx}
          value={level.selected}
          onValueChange={(next) => handleLevelChange(level, idx, next)}
          disabled={disabled ?? false}
        >
          <SelectTrigger data-testid={`${testId}-level-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {idx === 0 ? (
              <SelectItem value={NONE}>{noneLabel ?? t('pair_dialog.group_none')}</SelectItem>
            ) : (
              <SelectItem value={KEEP}>{t('group_cascader.keep_level')}</SelectItem>
            )}
            {level.options.map((node) => nodeOption(node))}
          </SelectContent>
        </Select>
      ))}
    </div>
  );
}
